import { NgClass } from '@angular/common';
import { Component, computed, input, InputSignal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { TrailingIcon } from '@shared/interfaces/navigation-item.interface';
import { AraAdaptiveTooltipDirective } from '../../../directives/ara-adaptive-tooltip.directive';

export type InfoBadgeVariant = 'beta' | 'alpha' | 'soon' | 'default';

@Component({
	selector: 'ara-info-badge',
	imports: [AraAdaptiveTooltipDirective, NgClass],
	templateUrl: './ara-info-badge.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './ara-info-badge.component.scss',
})
export class AraInfoBadgeComponent {
	public trailingIcon: InputSignal<TrailingIcon> = input(undefined);
	public readonly tooltipPosition = input<'below' | 'right'>('right');

	/** Optionales Label – überschreibt den `trailingIcon.title`-Anzeigetext.
	 *  Beispiel: araHk4-Sidenav nutzt `label="B"` / `label="S"` für kompakte Badges,
	 *  während der vollständige Titel weiterhin im Tooltip steht. */
	readonly label: InputSignal<string | undefined> = input<string | undefined>(undefined);

	/** Optionale Farb-Variant. Wenn nicht gesetzt, wird aus `trailingIcon.title`
	 *  automatisch ermittelt (BETA/ALPHA/SOON). */
	readonly variant: InputSignal<InfoBadgeVariant | undefined> = input<InfoBadgeVariant | undefined>(undefined);

	protected readonly displayLabel: Signal<string> = computed((): string => this.label() ?? this.trailingIcon()?.title ?? '');

	/** Ein-Zeichen-Badge → runder, zentrierter Kreis statt Pille (s. .badge-circle/.badge-pill). */
	protected readonly isSingleChar: Signal<boolean> = computed((): boolean => this.displayLabel().trim().length === 1);

	protected readonly effectiveVariant: Signal<InfoBadgeVariant> = computed((): InfoBadgeVariant => {
		const explicit: InfoBadgeVariant | undefined = this.variant();
		if (explicit) {
			return explicit;
		}
		const title: string | undefined = this.trailingIcon()?.title;
		if (title === 'BETA') return 'beta';
		if (title === 'ALPHA') return 'alpha';
		if (title === 'SOON') return 'soon';
		return 'default';
	});

	protected readonly Boolean = Boolean;
}
